<script setup>
import { computed } from 'vue'

const props = defineProps({
  placeholder: {
    type: String,
  },
  field: {
    type: String,
  },
  form: {
    type: Object,
  },
  // RED-286: opt-in Laravel Precognition. The field is checked against the
  // server rule on blur (and by the caller once the value looks complete), and
  // the hint line is always rendered so the neutral/red/green swap never moves
  // the form below it. Off by default, so every other caller is unchanged.
  liveValidate: {
    type: Boolean,
    default: false,
  },
})

const model = defineModel({ required: true })

const error = computed(() => props.form.errors[props.field])
// An emptied field is not "correct" — form.reset() clears the value but the
// precognition validator keeps its last verdict.
const isValid = computed(
  () =>
    props.liveValidate &&
    String(model.value ?? '').trim() !== '' &&
    props.form.valid?.(props.field) === true,
)

const clearError = () => {
  // A precognitive form keeps its own validator state, which clearErrors()
  // does not reach.
  if (props.form.forgetError) {
    props.form.forgetError(props.field)
  } else {
    props.form.clearErrors(props.field)
  }
}

const validateOnBlur = () => {
  if (props.liveValidate && String(model.value ?? '').trim() !== '') {
    props.form.validate(props.field)
  }
}
</script>

<template>
  <div class="input-group">
    <InputText
      v-model="model"
      :invalid="error"
      :placeholder="placeholder"
      @input="clearError"
      @blur="validateOnBlur"
    />
    <p
      v-if="liveValidate"
      class="text-xs mt-1 leading-4 min-h-8"
      :class="error ? 'text-red-500' : 'text-gray-500'"
    >
      <template v-if="error">{{ error }}</template>
      <slot v-else name="hint" :valid="isValid" />
    </p>
    <p v-else-if="error" class="text-red-500 text-sm break-normal">
      <small>{{ error }}</small>
    </p>
  </div>
</template>
